/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Divider, Flex, Text, TextField, toast } from '@servicetitan/anvil2';
import { TargetSuperHeatRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const TargetSuperHeatForm: FC<{
    className?: string;
    onSubmit?: (params: TargetSuperHeatRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            outdoor_air_temp: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            return_air_temp_wetbulb: new InputFieldState<string>(''),
            return_air_temp_drybulb: new InputFieldState<string>(''),
            return_air_humidity: new InputFieldState<string>(''),
        }).validators(form => {
            if (
                (!form.return_air_temp_wetbulb.value &&
                    form.return_air_temp_drybulb.value &&
                    form.return_air_humidity.value) ||
                (form.return_air_temp_wetbulb.value &&
                    !form.return_air_temp_drybulb.value &&
                    !form.return_air_humidity.value)
            ) {
                return null;
            }
            toast.danger({
                duration: 5000,
                title: 'Validation Error',
                message:
                    'Either Return Air Wet Bulb Temperature or both Return Air Dry Bulb Temperature and Return Air Relative Humidity are required',
            });
            return 'Either Return Air Wet Bulb Temperature or both Return Air Dry Bulb Temperature and Return Air Relative Humidity are required';
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: TargetSuperHeatRequestDto = {
            tool_name: 'target-super-heat',
            tool_input: {
                outdoor_air_temp: Number(formState.$.outdoor_air_temp.$),
                return_air_temp_wetbulb: Number(formState.$.return_air_temp_wetbulb.$) || undefined,
                return_air_temp_drybulb: Number(formState.$.return_air_temp_drybulb.$) || undefined,
                return_air_humidity: Number(formState.$.return_air_humidity.$) || undefined,
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                error={formState.$.outdoor_air_temp.error}
                label="Outdoor Dry Bulb Temperature (°F)"
                type="number"
                value={formState.$.outdoor_air_temp.value}
                onChange={e => formState.$.outdoor_air_temp.onChange(e.target.value)}
            />
            <Divider />
            <TextField
                label="Return Air Wet Bulb Temperature (°F)"
                type="number"
                value={formState.$.return_air_temp_wetbulb.value}
                onChange={e => formState.$.return_air_temp_wetbulb.onChange(e.target.value)}
            />
            <Text>OR</Text>
            <TextField
                label="Return Air Dry Bulb Temperature (°F)"
                type="number"
                value={formState.$.return_air_temp_drybulb.value}
                onChange={e => formState.$.return_air_temp_drybulb.onChange(e.target.value)}
            />
            <TextField
                label="Return Air Relative Humidity (%)"
                type="number"
                value={formState.$.return_air_humidity.value}
                onChange={e => formState.$.return_air_humidity.onChange(e.target.value)}
            />
            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
